import { GameINFOV2, LeagueItem, PartnerPageData } from 'types/types'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Share from 'assets/icons/Share'
import { KeyboardArrowRight, KeyboardArrowLeft } from '@mui/icons-material'
import { getCorrectMainFont } from '@/utils/getCorrectMainFont'
import {
  getStarSportsFontSize,
  getStarSportsFontStyle,
  getStarSportsHeaderText,
} from '@/utils/starSportsUtils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import twemoji from '@twemoji/api'
import LeagueDropdown from './LeagueDropdown'
import Details from '../assets/icons/Details'
import DropdownLeaguePicker from './DropdownLeaguePicker'

type LeagueCarouselProps = {
  leagues: LeagueItem[]
  page: PartnerPageData
  isUserJoined: boolean
  shouldShowLeagueDropdown: boolean
  game: GameINFOV2 | null
}

function CustomArrowNext(props: any) {
  const isDisabled = props.currentSlide === props.slideCount - 1
  const arrowColor = isDisabled ? '#353535' : 'white'
  return (
    <KeyboardArrowRight
      {...props}
      sx={{
        color: `${arrowColor} !important`,
        cursor: isDisabled ? 'not-allowed !important' : 'pointer !important',
      }}
    />
  )
}

function CustomArrowBack(props: any) {
  const isDisabled = props.currentSlide === 0
  const arrowColor = isDisabled ? '#353535' : 'white'
  return (
    <KeyboardArrowLeft
      {...props}
      sx={{
        color: `${arrowColor} !important`,
        cursor: isDisabled ? 'not-allowed !important' : 'pointer !important',
      }}
    />
  )
}

function LeagueCarousel({
  leagues,
  page,
  isUserJoined,
  shouldShowLeagueDropdown,
  game,
}: LeagueCarouselProps) {
  const router = useRouter()
  const leagueIndex = leagues.findIndex((league) => league.id === Number(router.query.id))
  const [currentLeague, setCurrentLeague] = useState<number>(leagueIndex)
  const currentLeagueData = useMemo(() => leagues[currentLeague], [currentLeague])
  const titleText = currentLeagueData?.name || game?.name || getStarSportsHeaderText(page)
  const titleHtml = useMemo(
    () =>
      twemoji.parse(titleText ?? '', {
        folder: 'svg',
        ext: '.svg',
        attributes: () => ({ onerror: 'this.onerror=null;this.replaceWith(this.alt)' }),
      }),
    [titleText],
  )

  const previousLeagueRef = useRef(currentLeague)
  useEffect(() => {
    if (previousLeagueRef.current === currentLeague) return
    previousLeagueRef.current = currentLeague

    const slug = router.query.slug as string
    if (currentLeague === -1 && game) {
      router.push(`/${slug}/games/${game.id}`)
    } else if (currentLeagueData) {
      router.push(`/${slug}/mini-league/${currentLeagueData.id}`)
    }
  }, [currentLeague])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const renderLeague = () => {
    const league = leagues[currentLeague]
    if (!league) return null
    return (
      <Box key={league.id}>
        <Box display="flex" justifyContent="center" alignItems="center" marginTop={1} gap={3}>
          <Link
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 16px',
              backgroundColor: 'transparent',
              border: '2px solid white',
              gap: '8px',
              minWidth: '120px',
              justifyContent: 'center',
              borderRadius: '8px',
            }}
            href={{ pathname: `/${router.query.slug}/games/${league?.id}/details` }}
          >
            <Details color="white" />
            <Typography fontSize="14px" color="white">
              League Details
            </Typography>
          </Link>

          <Link
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 16px',
              backgroundColor: 'transparent',
              border: '2px solid white',
              gap: '8px',
              width: '120px',
              justifyContent: 'center',
              borderRadius: '8px',
            }}
            href={{ pathname: `/${router.query.slug}/games/${league?.id}/share` }}
          >
            <Share color="white" />
            <Typography fontSize="14px" color="white">
              Share
            </Typography>
          </Link>
        </Box>
      </Box>
    )
  }
  return (
    <Box sx={{ maxWidth: isMobile ? '358px' : '900px' }}>
      <Box padding={1}>
        {/* eslint-disable-next-line react/no-danger */}
        <Typography
          component="div"
          margin={0}
          fontFamily={getCorrectMainFont(page)}
          fontWeight={900}
          fontSize={getStarSportsFontSize(page, 32)}
          lineHeight={1}
          fontStyle={getStarSportsFontStyle(page)}
          textTransform="uppercase"
          color={page.game_page_first_header_color ?? '#353535'}
          style={{
            backgroundColor: page.feature_flags.game_header_white_bg ? '#FFF' : undefined,
          }}
          sx={{
            '& img.emoji': { height: '1em', width: '1em', verticalAlign: '-0.1em' },
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: titleHtml }}
        />
        <Typography
          marginTop={2}
          marginBottom={2}
          fontWeight={700}
          lineHeight={1}
          color={page.secondary_header_color ?? '#353535'}
        >
          {currentLeagueData?.description || game?.description || page.header_line_2}
        </Typography>
        {leagues.length ? (
          <DropdownLeaguePicker
            onChange={(val) => setCurrentLeague(val)}
            value={currentLeague}
            options={leagues}
            page={page}
          />
        ) : (
          <></>
        )}
        {renderLeague() ?? <LeagueDropdown pageData={page} miniLeague={game} />}
      </Box>
    </Box>
  )
}
export default LeagueCarousel
